<script lang="ts">
  import LogoGraphic from "$assets/icons/logo-graphic.svelte";
  import { Button } from "$ui/button";
  import { environment, firebaseApp } from "$lib/utils/env";

  import { getAuth, OAuthProvider, signInWithPopup } from "firebase/auth";
  import { onMount } from "svelte";
  import FullPageShell from "$lib/components/layout/FullPageShell.svelte";

  const delegatedPermissions = [
    {
      description: "These allow us to sign users into Permissions Assurance",
      perms: ["email", "openid", "profile", "User.Read"],
    },
    {
      description: "This allows us to manage user sessions across multiple data sources",
      perms: ["offline_access"],
    },
    {
      description: "These ensure we only ever return data a user can access",
      perms: ["Files.Read", "Files.Read.All"],
    },
  ];
  const applicationPermissions = [
    {
      description: "This allows us to understand a users role in your organisation",
      perms: ["User.Read.All"],
    },
    {
      description: "This is how we scan your data and build up a semantic index",
      perms: ["Files.Read.All"],
    },
    {
      description: "These allow us to know which users are in which groups",
      perms: ["GroupMember.Read.All", "Sites.Read.All"],
    },
    {
      description: "This allows us to read data classification labels, to prioritise security recommendations",
      perms: ["InformationProtectionPolicy.Read.All"],
    },
  ];

  const urlParams = new URLSearchParams(window.location.search || window.location.hash.split("?")[1]);
  const successTenant = urlParams.get("tenant");
  let error = urlParams.get("error_description") || urlParams.get("error");

  // TODO: put this in the env.json?
  const sharepointAppClientIds = {
    production: "bba1c7b4-4bb7-4536-a928-d9c3ae346e88",
    staging: "0c825a8a-4927-4139-ab6a-4da6d10a984f",
    development: "4f6a8e56-1d54-46a5-b933-03dc557886f2",
  };
  const redirectUrl = `${window.location.origin}/#/admin-consent`;

  onMount(async () => {});

  async function initiateAdminSignIn() {
    const provider = new OAuthProvider("microsoft.com");
    provider.setCustomParameters({ prompt: "consent" });
    delegatedPermissions.forEach((permGroup) => {
      permGroup.perms.forEach((perm) => {
        provider.addScope(perm);
      });
    });

    const auth = getAuth(await firebaseApp);
    await signInWithPopup(auth, provider);
  }

  async function initiateAdminConsent() {
    const env = (await environment).environment;
    const clientId: string = (sharepointAppClientIds as any)[env];

    // Docs for v1 endpoint are hard to find?
    const adminConsentV1 = `https://login.microsoftonline.com/organizations/adminconsent
      ?client_id=${clientId}
      &redirect_uri=${redirectUrl}`;

    // https://learn.microsoft.com/en-us/entra/identity-platform/v2-admin-consent
    const adminConsentV2 = `https://login.microsoftonline.com/organizations/v2.0/adminconsent
      ?client_id=${clientId}
      &scope=.default
      &redirect_uri=${redirectUrl}`;

    // 500x600 is the size of the standard Microsoft popup login window
    const popup = window.open(adminConsentV2, "admin-consent", "width=500,height=600");
  }
</script>

<FullPageShell
  mainClass="overflow-auto max-h-[100svh] !pb-0 onboarding"
  class="flex flex-col items-center gap-8 md:gap-12"
  hasBackground={true}
>
  <div class="max-w-2xl mt-4 md:mt-12 flex flex-col items-center gap-8">
    <div class="[&>svg]:h-7 md:[&>svg]:h-12">
      <LogoGraphic />
    </div>
    <div class="flex flex-col items-center gap-1 md:gap-2 text-center">
      <h1>Connect to Sharepoint</h1>
      <p>In order to understand and secure your data we rely on certain access within your Entra/Sharepoint tenancy</p>
    </div>
    <div class="flex flex-col gap-1 md:gap-2 text-center">
      <h3>Delegated Permissions</h3>
      <p class="text-sm">
        <span class="font-thin italic">These are used on behalf of users</span>
      </p>
      <div class="grid grid-cols-2 gap-4">
        {#each delegatedPermissions as permGroup}
          <div class="justify-self-end text-right">
            <p>{permGroup.description}</p>
          </div>
          <div class="justify-self-start text-left col-span">
            <div class="span">
              {#each permGroup.perms ?? [] as perm}
                <div class="text-sm">
                  <span class="font-mono line-clamp-1">{perm}</span>
                </div>
              {/each}
            </div>
          </div>
        {/each}
      </div>
    </div>
    <div class="flex flex-col gap-1 md:gap-2 text-center">
      <h3>Application Permissions</h3>
      <p class="text-sm">
        <span class="font-thin italic">These are used by Permissions Assurance directly</span>
      </p>
      <div class="grid grid-cols-2 gap-4">
        {#each applicationPermissions as permGroup}
          <div class="justify-self-end text-right">
            <p>{permGroup.description}</p>
          </div>
          <div class="justify-self-start text-left col-span">
            <div class="span">
              {#each permGroup.perms ?? [] as perm}
                <div class="text-sm">
                  <span class="font-mono line-clamp-1">{perm}</span>
                </div>
              {/each}
            </div>
          </div>
        {/each}
      </div>
    </div>
    {#if error}
      <div class="flex flex-col items-center gap-1 md:gap-2 text-center">
        <div class="flex flex-col justify-center items-center p-8 card">
          <div class="flex flex-col gap-2 text-center">
            <div class="text-2xl font-semibold">Uh oh!</div>
            <p>{error}</p>
            <p>Depending on the error, you may be able to try again</p>
          </div>
        </div>
      </div>
    {/if}
    {#if successTenant && !error}
      <div class="flex flex-col items-center">
        <div class="flex flex-col justify-center items-center p-8 card">
          <div class="flex flex-col gap-2 text-center">
            <div class="text-2xl font-semibold">Success!</div>
            <p>You have connected Permissions Assurance to your Microsoft tenant</p>
          </div>
        </div>
      </div>
    {/if}
    {#if !successTenant}
      <div class="flex flex-col items-center gap-1 md:gap-2 text-center">
        <Button on:click={initiateAdminConsent}>Consent to Sharepoint</Button>
        <Button on:click={initiateAdminSignIn}>Sign in to Sharepoint</Button>
        <p>You will need an Entra administrator in your tenancy to do this.</p>
      </div>
    {/if}
    <div class="p-8 flex flex-row items-center gap-1 md:gap-2 text-center">
      {#await environment}
        <p>loading...</p>
      {:then env}
        <a href={env.legals.termsOfService}>Terms of Service</a>
        <a href={env.legals.privacyPolicy}>Privacy Policy</a>
      {/await}
    </div>
  </div>
</FullPageShell>
